import { App, type FormInstance, type FormProps } from "antd"
import dayjs from "dayjs"
import { arrayRemove, arrayUnion } from "firebase/firestore"
import { useStore } from "jotai"
import { cloneDeep, omit, pick, pickBy } from "lodash"
import { useCallback, useRef } from "react"

import {
  classAtoms,
  classSessionAtoms,
  curriculumAtoms,
  studentAtoms,
} from "#atoms"
import Flex from "#blocks/Flex"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import { Class } from "#classes/Class"
import Log from "#classes/Log"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import TryoutResultTag from "#components/TryoutResultTag"
import { CLASS_GROUPS } from "#constants/classOptions"
import { hasSuppliesFee } from "#constants/feeConstants"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import {
  ALL_SKILL_LEVEL_CUSTOMER_LABELS,
  CLASS_GROUP_TO_SKILL_TYPE,
} from "#constants/skillLevelConstants"
import { TRYOUT_FEES } from "#constants/tryoutOptions"
import { useApiContext } from "#contexts/ApiContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  AddTryoutReceipt,
  TRYOUT_RECEIPT_TYPES,
  TryoutFollowupPassReceipt,
} from "#customTypes/composedTypes/emailTypes"
import {
  DOC_TYPES,
  FirebaseBatchUpdate,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  STUDENT_TASK_TYPES,
  StudentTask,
} from "#customTypes/composedTypes/studentTaskTypes"
import {
  StudentConstructor,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import {
  AddTryoutFormValues,
  EditTryoutProgressFormData,
  ScheduledEnrollmentEmail,
  STEP_2_SKILL_FIELD_BY_TYPE,
  TryoutConstructor,
  TryoutProgressFormData,
} from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  AttendanceClassSessions,
  DatabaseObject,
  invariant,
} from "#customTypes/types"
import TryoutProgressForm, {
  type StepSetter,
} from "#forms/TryoutProgressForm/TryoutProgressForm"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import {
  TransactionData,
  useFirebaseBatchUpdateDynamic,
  useFirebaseTransaction,
} from "#hooks/reactQuery/useFirebaseMutation"
import {
  buildClassEnrollmentUpdate,
  getSessionInfoFromId,
} from "#utils/classHelpers"
import {
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
  STANDARD_DAY_FORMAT,
  STANDARD_TIME_FORMAT,
} from "#utils/dateHelpers"
import { getStudentRecipients } from "#utils/emailHelpers"
import { formatSemester, getCurrentSemester } from "#utils/semesterHelpers"
import {
  addNewSkillEntry,
  getBlankAttendance,
  hasRealEnrollmentHistory,
  isStillEnrolled,
} from "#utils/studentHelpers"
import { getSkillLevelAsOf } from "#utils/studentSkillHelpers"
import {
  formatTryoutEnrollmentBlock,
  getAggregateStatus,
  getTryoutEnrollmentBlock,
  getTryoutResult,
} from "#utils/tryoutHelpers"

import useTryoutProgressFormData from "./useTryoutProgressFormData"

const GUARDIAN_FIELD_KEYS = [
  "guardian1Name",
  "guardian1Phone",
  "guardian1Email",
  "guardian2Name",
  "guardian2Phone",
  "guardian2Email",
  "primaryAddress",
] as const

export interface TryoutProgressFormModalProps {
  onClose: () => void
  tryoutId: Uuid | undefined
}

const TryoutProgressFormModal = ({
  onClose,
  tryoutId,
}: TryoutProgressFormModalProps) => {
  const {
    tryout,
    student,
    tryoutSession,
    tryoutClass,
    isReady,
    initialFormState,
  } = useTryoutProgressFormData({
    tryoutId,
  })

  const { canRead, canUpdate, canDelete } = useResourcePermissions(
    RESOURCES.TRYOUT
  )
  const { currentUserId } = useUserContext()

  const setStepRef = useRef<StepSetter | undefined>(undefined)

  const { mutate: updateTryoutProgress } = useFirebaseBatchUpdateDynamic()

  const store = useStore()
  const { apiHandler } = useApiContext()
  const { modal, message } = App.useApp()

  // Errors handled in handleSubmit
  const handleSendReceipt = useCallback(
    async (
      formValues: AddTryoutFormValues | TryoutProgressFormData,
      isDelete: boolean = false
    ) => {
      const { studentId, sessionId } = formValues
      // Get tryoutFeeId from step0 (new structure) or top level (old structure)
      const tryoutFeeId: Uuid | undefined = formValues.step0?.tryoutFeeId
      const isTryoutDateChanged = sessionId !== initialFormState?.sessionId

      const selectedStudent = store.get(studentAtoms.docByIdAtom(studentId))
      const session = store.get(classSessionAtoms.docByIdAtom(sessionId))

      if (!session || !selectedStudent) {
        message.error("Data for sending email confirmation not found.")
        return
      }

      const recipients = getStudentRecipients(selectedStudent)
      if (!recipients.length) {
        message.error("No recipients found to send confirmation email.")
        return
      }

      const sessionDate = dayjs(session.eventStart).format(STANDARD_DATE_FORMAT)
      const sessionDay = dayjs(session.eventStart).format(STANDARD_DAY_FORMAT)
      const sessionStartTime = dayjs(session.eventStart).format(
        STANDARD_TIME_FORMAT
      )
      const sessionEndTime = dayjs(session.eventEnd).format(
        STANDARD_TIME_FORMAT
      )

      const receiptData: AddTryoutReceipt = {
        recipients,
        sendTime: dayjs().format(STANDARD_DATE_TIME_FORMAT),

        studentName: selectedStudent.name,
        studentDob: dayjs(selectedStudent.dob).format(STANDARD_DATE_FORMAT),

        sessionDate,
        sessionDay,
        sessionStartTime,
        sessionEndTime,
        // Leave this as plural because the template expects it
        tryoutFees: tryoutFeeId
          ? `${TRYOUT_FEES.find((fee) => fee.id === tryoutFeeId)?.label ?? ""}`
          : "",
        isTryoutFeePaid: formValues.step0?.isTryoutFeePaid ?? false,
      }

      if (isDelete) {
        modal.confirm({
          title: "Tryout deleted! Would you like to send a cancellation email?",
          onOk: async () => {
            await apiHandler.sendReceiptEmail(
              receiptData,
              TRYOUT_RECEIPT_TYPES.TRYOUT_CANCELLATION
            )
            message.success("Cancellation email sent")
          },
          onCancel: () => {},
          okText: "Yes, send email",
          cancelText: "No",
        })
      } else if (isTryoutDateChanged) {
        modal.confirm({
          title:
            "Tryout date changed! Would you like to send an email confirmation?",
          onOk: async () => {
            await apiHandler.sendReceiptEmail(
              receiptData,
              TRYOUT_RECEIPT_TYPES.TRYOUT_CHANGE
            )
            message.success("Confirmation email sent")
          },
          onCancel: () => {},
          okText: "Yes, send email",
          cancelText: "No",
        })
      }
    },
    [apiHandler, store, initialFormState?.sessionId, modal, message]
  )

  // Navigate to the step that has the validation error
  type FinishFailedInfo = Parameters<
    NonNullable<FormProps<TryoutProgressFormData>["onFinishFailed"]>
  >[0]
  const handleFinishFailed: FormProps<TryoutProgressFormData>["onFinishFailed"] =
    useCallback(
      (errorInfo: FinishFailedInfo) => {
        const firstError = errorInfo.errorFields[0]
        if (!firstError) return

        const firstFieldName = firstError.name
        const fieldPrefix = Array.isArray(firstFieldName)
          ? (firstFieldName[0] as string)
          : (firstFieldName as string)

        const stepMap: Record<string, 0 | 1 | 2 | 3> = {
          step0: 0,
          step1: 1,
          step2: 2,
          _skillLevelRequired: 2,
          step3: 3,
        }

        const targetStep = stepMap[fieldPrefix]
        if (targetStep != null) {
          setStepRef.current?.(targetStep)
        }

        message.warning(
          firstError.errors?.[0] ?? "Please fix form errors before submitting."
        )
      },
      [message]
    )

  const handleFinish = async (
    cleanedValues: TryoutProgressFormData & DatabaseObject,
    form?: FormInstance
  ): Promise<void> => {
    if (!canUpdate) {
      throw new Error("You do not have permission to update tryouts")
    }

    invariant(
      !!(student && tryoutSession && tryoutId && tryoutClass),
      "Missing data to update tryout. Try again in a moment."
    )

    const oldSessionId = initialFormState?.sessionId
    const newSessionId = cleanedValues.sessionId
    const didSessionChange = oldSessionId !== newSessionId

    // Strip student-only fields (guardian/address) from the tryout save
    const step1Data =
      (cleanedValues.step1 as EditTryoutProgressFormData["step1"]) ?? {}
    const tryoutStep1 = omit(step1Data, GUARDIAN_FIELD_KEYS)

    const updatedTryout = omit(
      {
        ...cleanedValues,
        step1: tryoutStep1,
        tryoutFilterDate: cleanedValues.tryoutFilterDate.toISOString(),
        tryoutStatus: getAggregateStatus(cleanedValues),
      },
      // Strip FeeSelector UI-only fields and validation-only fields
      [
        "tuitionOverride",
        "discountOverride",
        "suppliesOverride",
        "materialsOverride",
        "_skillLevelRequired",
      ]
    ) as unknown as TryoutConstructor

    const [classId, sessionIndex] = getSessionInfoFromId(
      cleanedValues.sessionId
    )

    const newAttendance = cloneDeep(student?.attendance ?? {})
    newAttendance[classId] ??= getBlankAttendance(tryoutClass?.sessionIds)

    const existingStatus = newAttendance[classId][sessionIndex].status
    const hasTryoutAttendance =
      existingStatus === ATTENDANCE_STATUSES.TRYOUT_PRESENT ||
      existingStatus === ATTENDANCE_STATUSES.TRYOUT_ABSENT ||
      existingStatus === ATTENDANCE_STATUSES.TRYOUT_TARDY
    // The followup email gate uses a stricter subset: we only ship the
    // "you passed your tryout" email when the student actually showed up.
    // TRYOUT_ABSENT and the pre-attendance statuses don't qualify.
    const hasAttendedTryout =
      existingStatus === ATTENDANCE_STATUSES.TRYOUT_PRESENT ||
      existingStatus === ATTENDANCE_STATUSES.TRYOUT_TARDY
    if (!hasTryoutAttendance) {
      newAttendance[classId][sessionIndex].status =
        ATTENDANCE_STATUSES.TRYOUT_ENROLLED
    }

    // Handle changed date
    const isTryoutDateChanged =
      initialFormState?.sessionId &&
      cleanedValues.sessionId !== initialFormState?.sessionId
    if (isTryoutDateChanged) {
      const [oldClassId, oldSessionIndex] = getSessionInfoFromId(
        initialFormState.sessionId
      )
      newAttendance[oldClassId][oldSessionIndex].status =
        ATTENDANCE_STATUSES.NOT_ENROLLED

      if (!isStillEnrolled(newAttendance[oldClassId])) {
        delete newAttendance[oldClassId]
      }
    }

    // Sync guardian/address info from Step 1 back to student
    const guardianFields = pickBy(
      pick(step1Data, GUARDIAN_FIELD_KEYS),
      (v) => v != null
    )

    const updatedStudent: Partial<StudentConstructor> = {
      attendance: newAttendance,
      ...guardianFields,
    }

    const submitData: FirebaseBatchUpdate = {
      [DOC_TYPES.STUDENTS]: {
        [student.id]: updatedStudent,
      },
      [DOC_TYPES.CLASS_SESSIONS]: {
        [newSessionId]: {
          tryoutIds: arrayUnion(tryoutId),
        },
      },
      [DOC_TYPES.TRYOUTS]: {
        [tryoutId]: updatedTryout,
      },
    }

    if (didSessionChange && oldSessionId) {
      submitData[DOC_TYPES.CLASS_SESSIONS]![oldSessionId] = {
        tryoutIds: arrayRemove(tryoutId),
      }
    }

    // Update student skill history if level was set in Step 2
    const step2 = cleanedValues.step2 as EditTryoutProgressFormData["step2"]
    const classGroup = tryoutClass.group ?? CLASS_GROUPS.CORE
    const skillType =
      CLASS_GROUP_TO_SKILL_TYPE[classGroup] ?? StudentSkillType.core
    // Step 2's dropdowns are named for the ladder they edit.
    const skillFieldName = STEP_2_SKILL_FIELD_BY_TYPE[skillType]

    const skillLevel =
      step2?.[skillFieldName] ?? getSkillLevelAsOf(student, skillType)
    if (step2?.didPassTryout === true && step2?.isLevelUpdated && skillLevel) {
      const updatedSkillHistory = addNewSkillEntry(
        student.skillHistory ?? {},
        getCurrentSemester(),
        { [skillType]: skillLevel }
      )

      submitData[DOC_TYPES.STUDENTS]![student.id] = {
        ...submitData[DOC_TYPES.STUDENTS]![student.id],
        skillHistory: updatedSkillHistory,
      }
    }

    // Handle Step 3 enrollment changes (enroll or unenroll)
    const step3 = cleanedValues.step3 as EditTryoutProgressFormData["step3"]
    const selectedClassId = step3?.selectedClassId
    const selectedSessionIds = step3?.selectedSessionIds ?? []
    const hasExistingClassAttendance =
      selectedClassId && newAttendance[selectedClassId] != null
    let updatedClassAttendance = selectedClassId
      ? newAttendance[selectedClassId]
      : undefined

    if (
      selectedClassId &&
      (selectedSessionIds.length > 0 || hasExistingClassAttendance)
    ) {
      // Get the enrollment class data from store
      const enrollmentClass = store.get(classAtoms.docByIdAtom(selectedClassId))

      // Enrollment gate: a failed tryout can't enroll, and a non-camp
      // enrollment needs a skill level in the class group (JARVIS-43). Check
      // against the student as it will be after this submit (Step 2 may have
      // just set the level in submitData). Only enforced while sessions remain
      // selected so un-enrolling is never blocked; Step 3 renders the same
      // helper as a warning so the user sees it before saving.
      if (selectedSessionIds.length > 0) {
        const studentAfterSubmit = {
          ...student,
          ...submitData[DOC_TYPES.STUDENTS]![student.id],
        }
        const enrollmentBlock = getTryoutEnrollmentBlock({
          didPassTryout: step2?.didPassTryout,
          student: studentAfterSubmit,
          enrollmentClass,
          hasExistingEnrollment: hasRealEnrollmentHistory(
            student.attendance?.[selectedClassId]
          ),
        })
        if (enrollmentBlock) {
          setStepRef.current?.(enrollmentBlock.step)
          throw new Error(formatTryoutEnrollmentBlock(enrollmentBlock))
        }
      }

      if (enrollmentClass?.sessionIds?.length) {
        // Build selected session indexes (boolean[]) from selectedSessionIds
        const selectedSessionIndexes = enrollmentClass.sessionIds.map(
          (sessionId) => selectedSessionIds.includes(sessionId)
        )

        // Get existing attendance for the enrollment class
        const existingAttendance =
          newAttendance[selectedClassId] ??
          getBlankAttendance(enrollmentClass.sessionIds)

        // Get session docs for the enrollment class
        const enrollmentSessions = enrollmentClass.sessionIds.map((id) => {
          const session = store.get(classSessionAtoms.docByIdAtom(id))
          return session ?? { id }
        })

        // Build enrollment update — mutates newAttendance directly
        const {
          class: enrollmentClassData,
          classSessionsById: enrollmentSessionsById,
          newAttendanceForClass: newClassAttendance,
        } = buildClassEnrollmentUpdate(
          selectedSessionIndexes,
          enrollmentSessions,
          existingAttendance,
          selectedClassId,
          student.id,
          newAttendance, // mutated in place: class key added or deleted
          "student"
        )
        updatedClassAttendance = newClassAttendance ?? undefined

        // Update student attendance in submit data (newAttendance was mutated)
        submitData[DOC_TYPES.STUDENTS]![student.id] = {
          ...submitData[DOC_TYPES.STUDENTS]![student.id],
          attendance: newAttendance,
        }

        // Set class data
        submitData[DOC_TYPES.CLASSES] = submitData[DOC_TYPES.CLASSES] ?? {}
        submitData[DOC_TYPES.CLASSES][selectedClassId] = enrollmentClassData

        // Set class session data
        Object.entries(enrollmentSessionsById).forEach(
          ([sessionId, sessionData]) => {
            submitData[DOC_TYPES.CLASS_SESSIONS]![sessionId] = {
              ...submitData[DOC_TYPES.CLASS_SESSIONS]![sessionId],
              ...sessionData,
            }
          }
        )

        // The payment task and enrollmentReceiptSent / scheduledEnrollmentEmail
        // flags are written from handleSendEnrollmentReceipt after the email
        // API returns. The payment task uses a deterministic id
        // (`${tryoutId}_payment`) so arrayUnion is idempotent across retries.
      }
    }

    await updateTryoutProgress(
      submitData,
      Object.keys(submitData) as DOC_TYPES[]
    )

    // Send enrollment receipt if requested.
    // Belt-and-suspenders: also skip when the followup is already sent or
    // currently scheduled at Mandrill, so a stale `sendReceipt: true` (e.g.
    // from a window that loaded before the post-send write committed) can't
    // dispatch a duplicate email and orphan the existing mandrillIds.
    // The hasAttendedTryout gate is the hard requirement: we only invite a
    // student to enroll after they actually attended the tryout.
    if (
      step3?.sendReceipt &&
      hasAttendedTryout &&
      !step3?.enrollmentReceiptSent &&
      !step3?.scheduledEnrollmentEmail &&
      selectedClassId &&
      selectedSessionIds.length > 0
    ) {
      await handleSendEnrollmentReceipt(
        cleanedValues,
        selectedClassId,
        updatedClassAttendance,
        skillLevel,
        form
      )
    }
  }

  // Send enrollment receipt for Step 3 class enrollment
  const handleSendEnrollmentReceipt = useCallback(
    async (
      formValues: TryoutProgressFormData,
      enrollmentClassId: Uuid,
      attendanceForEnrollmentClass: AttendanceClassSessions | undefined,
      studentSkillLevel: string | undefined,
      form?: FormInstance
    ) => {
      try {
        const enrollmentClass = store.get(
          classAtoms.docByIdAtom(enrollmentClassId)
        )
        const student = store.get(
          studentAtoms.docByIdAtom(formValues.studentId)
        )
        const tryoutSessionForReceipt = store.get(
          classSessionAtoms.docByIdAtom(formValues.sessionId)
        )

        invariant(enrollmentClass != null, "Class data not found for receipt")
        invariant(student != null, "Student data not found for receipt")

        const curriculum = store.get(
          curriculumAtoms.docByIdAtom(enrollmentClass.curriculumId)
        )
        invariant(curriculum != null, "Curriculum data not found for receipt")

        const classSessions =
          enrollmentClass.sessionIds
            ?.map((id) => store.get(classSessionAtoms.docByIdAtom(id)))
            .filter(Boolean) ?? []

        const recipients = getStudentRecipients(student)
        invariant(recipients.length > 0, "No recipients found for receipt")

        const step3 = formValues.step3
        const classGroup = curriculum?.group ?? CLASS_GROUPS.CORE

        invariant(studentSkillLevel != null, "Student skill level not set!")

        // Get class display name
        const classDisplayName = new Class(
          enrollmentClass as ClassConstructor
        ).getDisplayName()

        const enrolledSessionIds =
          enrollmentClass.sessionIds?.filter((_, index) => {
            const status = attendanceForEnrollmentClass?.[index]?.status
            return status === ATTENDANCE_STATUSES.ENROLLED
          }) ?? []

        const enrolledSessions = classSessions.filter((s) =>
          enrolledSessionIds.includes(s?.id ?? "")
        )
        const firstSession = enrolledSessions[0]
        const lastSession = enrolledSessions[enrolledSessions.length - 1]

        // Determine if new enrollment or renewal
        // const existingAttendance = student.attendance?.[enrollmentClassId]
        // const isRenewal = hasRealEnrollmentHistory(existingAttendance)

        const {
          tuitionTotal = 0,
          discountAmount = 0,
          suppliesFee = 0,
        } = step3?.feeBreakdown ?? {}
        const tuitionDueWithoutSupplies = Math.max(
          0,
          tuitionTotal - discountAmount
        )
        const suppliesCost = hasSuppliesFee(classGroup) ? suppliesFee : 0
        const totalDueWithSupplySet = tuitionDueWithoutSupplies + suppliesCost

        // Schedule the email for 30 min after the tryout session ends.
        // Send immediately only if that target is already in the past.
        const targetSendAt = tryoutSessionForReceipt?.eventEnd
          ? dayjs(tryoutSessionForReceipt.eventEnd).add(30, "minute")
          : null
        const shouldSchedule = targetSendAt?.isAfter(dayjs()) ?? false
        const sendAtIso = shouldSchedule
          ? targetSendAt!.toISOString()
          : undefined

        const receipt: TryoutFollowupPassReceipt = {
          recipients,
          sendTime: dayjs().format(STANDARD_DATE_TIME_FORMAT),
          sendAt: sendAtIso,

          studentFirstName: student.firstName,
          studentLastName: student.lastName,
          startDate: firstSession
            ? dayjs(firstSession.eventStart).format(STANDARD_DATE_FORMAT)
            : "",
          classGroup,
          prettyClassName: classDisplayName,
          startTime: firstSession?.eventStart
            ? dayjs(firstSession.eventStart).format(STANDARD_TIME_FORMAT)
            : "",
          endTime: lastSession?.eventEnd
            ? dayjs(lastSession.eventEnd).format(STANDARD_TIME_FORMAT)
            : "",
          skillLevel:
            ALL_SKILL_LEVEL_CUSTOMER_LABELS[
              studentSkillLevel as keyof typeof ALL_SKILL_LEVEL_CUSTOMER_LABELS
            ],
          semester: formatSemester(enrollmentClass.semester),
          sessionCount: enrolledSessionIds.length,
          firstSessionDate: firstSession?.eventStart
            ? dayjs(firstSession.eventStart).format(STANDARD_DATE_FORMAT)
            : "",
          lastSessionDate: lastSession?.eventEnd
            ? dayjs(lastSession.eventEnd).format(STANDARD_DATE_FORMAT)
            : "",
          tuition: `$${tuitionDueWithoutSupplies}`,
          materialsCost: `$${suppliesCost}`,
          total: `$${totalDueWithSupplySet}`,
        }

        const { mandrillIds, sendAt } = await apiHandler.sendReceiptEmail(
          receipt,
          TRYOUT_RECEIPT_TYPES.TRYOUT_FOLLOWUP_PASS
        )

        // Sync form state immediately after Mandrill accepts but BEFORE the
        // Firestore write. If Firestore fails, the form still knows the email
        // was sent, preventing duplicate sends on retry. Mirrors the pattern
        // used by handleCancelClick in TryoutProgressStep3.
        if (form) {
          form.setFieldValue(["step3", "enrollmentReceiptSent"], true)
          form.setFieldValue(["step3", "sendReceipt"], false)
          if (sendAt && mandrillIds.length > 0) {
            form.setFieldValue(["step3", "scheduledEnrollmentEmail"], {
              mandrillIds,
              sendAt,
            })
          }
        }

        // Build the payment task with a deterministic id keyed by tryoutId so
        // arrayUnion dedupes across retries. Co-located with the email send so
        // the task only appears once Mandrill accepts — we don't want to bill
        // parents for an email that never went out.
        let paymentTask: StudentTask | null = null
        if (
          tryoutId &&
          attendanceForEnrollmentClass &&
          enrollmentClass.sessionIds
        ) {
          const firstEnrolledIndex = attendanceForEnrollmentClass.findIndex(
            (att) => att.status === ATTENDANCE_STATUSES.ENROLLED
          )
          if (firstEnrolledIndex >= 0) {
            const firstEnrolledSessionId =
              enrollmentClass.sessionIds[firstEnrolledIndex]
            const firstEnrolledSession = store.get(
              classSessionAtoms.docByIdAtom(firstEnrolledSessionId)
            )
            if (firstEnrolledSession?.eventStart != null) {
              const firstSessionDate = dayjs(
                firstEnrolledSession.eventStart
              ).format("MMM D")
              paymentTask = {
                id: `${tryoutId}_payment`,
                label: `${firstSessionDate}: $${totalDueWithSupplySet} Due`,
                type: STUDENT_TASK_TYPES.PURCHASE,
                isCompleted: false,
                createdAt: dayjs(firstEnrolledSession.eventStart).toISOString(),
                createdBy: currentUserId!,
                completedAt: null,
                completedBy: null,
              }
            }
          }
        }

        // Persist the outcome to the tryout doc. Optimistically flip
        // enrollmentReceiptSent regardless of whether the send was immediate
        // or scheduled — once Mandrill accepted the request the followup is
        // out of our hands. handleCancelScheduledEmail flips it back if the
        // teacher cancels before Mandrill ships it.
        // Dot-notation keys so Firestore merges into step3 instead of
        // replacing the whole map (which would wipe hasEvalFeedback,
        // hasGivenSwag, selectedClassId, feeBreakdown, etc.).
        if (tryoutId) {
          const tryoutPatch: Record<string, unknown> = {
            "step3.enrollmentReceiptSent": true,
            // Always clear the request flag — otherwise the next save would
            // re-trigger handleSendEnrollmentReceipt, sending a duplicate and
            // orphaning these mandrillIds (no longer cancelable).
            "step3.sendReceipt": false,
          }
          if (sendAt && mandrillIds.length > 0) {
            tryoutPatch["step3.scheduledEnrollmentEmail"] = {
              mandrillIds,
              sendAt,
            }
          }

          const batch: Record<string, Record<string, unknown>> = {
            [DOC_TYPES.TRYOUTS]: { [tryoutId]: tryoutPatch },
          }
          const docTypes: DOC_TYPES[] = [DOC_TYPES.TRYOUTS]
          if (paymentTask) {
            batch[DOC_TYPES.STUDENTS] = {
              [student.id]: { tasks: arrayUnion(paymentTask) },
            }
            docTypes.push(DOC_TYPES.STUDENTS)
          }

          await updateTryoutProgress(
            batch as unknown as FirebaseBatchUpdate,
            docTypes
          )
        }

        message.success(
          sendAt
            ? `Enrollment receipt scheduled for ${dayjs(sendAt).format(
                "ddd MMM D, h:mm A"
              )}`
            : "Enrollment receipt sent"
        )
      } catch (err) {
        console.error("Failed to send enrollment receipt", err)
        throw err
      }
    },
    [apiHandler, store, message, tryoutId, updateTryoutProgress, currentUserId]
  )

  // Cancel an already-scheduled (not yet sent) enrollment email.
  // Re-throws on failure so the caller (handleCancelClick) can skip the
  // form-state sync — otherwise the form would falsely advertise the cancel
  // succeeded while Firestore still has the scheduled send.
  // Returns the per-message tally so the caller can distinguish a clean
  // cancel from an "already shipped" race (Mandrill returned Unknown_Message
  // because sendAt slipped past while the cancel HTTP roundtrip was in flight).
  const handleCancelScheduledEmail = useCallback(
    async (
      scheduled: ScheduledEnrollmentEmail
    ): Promise<{ canceledCount: number; alreadyGoneCount: number }> => {
      if (!tryoutId) return { canceledCount: 0, alreadyGoneCount: 0 }

      try {
        const { canceled, alreadyGone } =
          await apiHandler.cancelScheduledReceipt(scheduled.mandrillIds)
        const anyShipped = alreadyGone.length > 0

        // Dot-notation keys so Firestore merges into step3 instead of
        // replacing the whole map. If any message was already delivered we
        // keep enrollmentReceiptSent=true so the UI reflects reality (parent
        // received the email). Always clear the scheduled handle since
        // nothing is cancelable anymore.
        const tryoutPatch: Record<string, unknown> = {
          "step3.scheduledEnrollmentEmail": null,
          "step3.enrollmentReceiptSent": anyShipped,
          "step3.sendReceipt": false,
        }

        await updateTryoutProgress(
          {
            [DOC_TYPES.TRYOUTS]: {
              [tryoutId]: tryoutPatch,
            },
          } as unknown as FirebaseBatchUpdate,
          [DOC_TYPES.TRYOUTS]
        )

        return {
          canceledCount: canceled.length,
          alreadyGoneCount: alreadyGone.length,
        }
      } catch (err) {
        console.error("Failed to cancel scheduled enrollment email", err)
        throw err
      }
    },
    [apiHandler, tryoutId, updateTryoutProgress]
  )

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!canDelete) {
      throw new Error("You do not have permission to delete tryouts")
    }

    // Prevent deletion once the student has attended the tryout session
    if (student && tryoutSession) {
      const [classId, sessionIndex] = getSessionInfoFromId(tryoutSession.id)
      const status = student.attendance?.[classId]?.[sessionIndex]?.status
      const attended =
        status === ATTENDANCE_STATUSES.TRYOUT_PRESENT ||
        status === ATTENDANCE_STATUSES.TRYOUT_ABSENT ||
        status === ATTENDANCE_STATUSES.TRYOUT_TARDY
      if (attended) {
        throw new Error(
          "This tryout cannot be deleted because the student has already attended the session."
        )
      }
    }

    if (
      !initialFormState ||
      !student ||
      !tryoutSession ||
      !tryoutId ||
      !isReady
    )
      throw new Error("Missing data to delete tryout. Try again in a moment.")

    const [classId, sessionIndex] = getSessionInfoFromId(tryoutSession.id)

    const attendance = cloneDeep(student.attendance ?? {})

    invariant(
      attendance[classId] != null,
      `Attendance for class ${classId} not found for ${Log.student(student)}!`
    )

    attendance[classId][sessionIndex].status = ATTENDANCE_STATUSES.NOT_ENROLLED

    if (!isStillEnrolled(attendance[classId])) {
      delete attendance[classId]
    }

    // Clean up tryout eval from student.files.tryoutEvals (keep waiver)
    const updatedFiles = cloneDeep(student.files ?? {})
    if (updatedFiles.tryoutEvals?.[tryoutId]) {
      delete updatedFiles.tryoutEvals[tryoutId]
    }

    const transactions: TransactionData[] = [
      {
        docType: DOC_TYPES.TRYOUTS,
        id: tryoutId,
        data: null,
        action: "delete",
      },
      {
        docType: DOC_TYPES.CLASS_SESSIONS,
        id: tryoutSession.id,
        data: {
          tryoutIds: arrayRemove(tryoutId),
        },
        action: "update",
      },
      {
        docType: DOC_TYPES.STUDENTS,
        id: student.id,
        data: {
          attendance,
          files: updatedFiles,
        },
        action: "update",
      },
    ]

    await handleTransaction(transactions)
    await handleSendReceipt(initialFormState, true)

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  if (!canRead) {
    return <div>You do not have permission to read tryouts</div>
  }

  if (!isReady || !initialFormState) return <div>Loading...</div>

  // The Pass/Fail buttons in Step 2 write step2.didPassTryout to Firestore
  // immediately, so reading the live tryout doc keeps this header in sync
  // with the table's Result column without waiting for a form submit.
  const title = student ? (
    <Flex style={{ alignItems: "center" }} gap={8}>
      Tryout for
      <ClickableStudentInfoRow student={student} border />
      <TryoutResultTag result={getTryoutResult(tryout?.step2?.didPassTryout)} />
    </Flex>
  ) : (
    "Tryout Progress"
  )

  return (
    <GenericFormModal<TryoutProgressFormData, TryoutProgressFormData>
      title={title}
      onClose={onClose}
      initialValues={initialFormState}
      onFinish={
        handleFinish as (values: TryoutProgressFormData) => Promise<void>
      }
      onFinishFailed={handleFinishFailed}
      resource={RESOURCES.TRYOUT}
      onDelete={handleDelete}
    >
      <TryoutProgressForm
        initialValues={initialFormState}
        isReady={isReady}
        stepRef={setStepRef}
        onCancelScheduledEmail={handleCancelScheduledEmail}
      />
    </GenericFormModal>
  )
}

export default TryoutProgressFormModal
